<script>
import { required, email } from "vuelidate/lib/validators";
import { authMethods } from "@/state/helpers";
import { renderizarBotonGoogle } from "@/services/googleIdentity";

/**
 * Acceso de clientes al portal.
 *
 * Usa la misma cuenta que la app móvil: se autentica contra Firebase y después
 * exige un documento en `app_usuarios`. Es el espejo del login del panel
 * (`views/account/login.vue`), que en cambio exige estar en `empleados`.
 */
export default {
  page: { title: "Entrar a Recnet" },
  data() {
    return {
      email: "",
      password: "",
      enviado: false,
      intentando: false,
      intentandoGoogle: false,
      googleListo: false,
      googleError: null,
      error: null,
    };
  },
  validations: {
    email: { required, email },
    password: { required },
  },
  computed: {
    /** El guard manda aquí a quien tiene cuenta de Firebase pero no perfil. */
    sinPerfil() {
      return this.$route.query.sinPerfil === "1";
    },
  },
  methods: {
    ...authMethods,

    /**
     * Los gestores de contraseñas y el autocompletado del navegador escriben
     * directamente en el DOM, sin disparar el evento que escucha `v-model`.
     * El campo se ve lleno pero el componente lo cree vacío, así que la
     * validación reclamaba "escribe tu correo" sobre un correo ya escrito.
     */
    sincronizarAutocompletado() {
      const campos = [
        { ref: "campoEmail", modelo: "email" },
        { ref: "campoPassword", modelo: "password" },
      ];
      for (const { ref, modelo } of campos) {
        const componente = this.$refs[ref];
        const elemento = componente && componente.$el;
        if (elemento && elemento.value !== this[modelo]) this[modelo] = elemento.value;
      }
    },

    async entrar() {
      this.sincronizarAutocompletado();
      this.enviado = true;
      this.$v.$touch();
      if (this.$v.$invalid) return;

      this.intentando = true;
      this.error = null;

      try {
        await this.logIn({ email: this.email, password: this.password });
        await this.entrarSiTienePerfil();
      } catch (err) {
        this.error = await this.mensajeDeErrorDeCredenciales(err);
      } finally {
        this.intentando = false;
      }
    },

    /**
     * Cuando Firebase rechaza las credenciales no dice por qué: la misma
     * respuesta cubre "no existe", "contraseña incorrecta" y "esta cuenta se
     * creó con Google y no tiene contraseña". Preguntar por los métodos de
     * acceso del correo es lo que permite dar una indicación útil en vez de
     * afirmar algo que puede ser falso.
     */
    async mensajeDeErrorDeCredenciales(err) {
      const codigo = err && err.code;
      const ambiguos = [
        "auth/user-not-found",
        "auth/wrong-password",
        "auth/invalid-credential",
        "auth/invalid-login-credentials",
      ];

      if (!ambiguos.includes(codigo)) return this.mensajeDeError(err);

      console.error("[portal] credenciales rechazadas:", err);
      const metodos = await this.metodosDeAcceso(this.email);

      if (metodos.includes("google.com") && !metodos.includes("password")) {
        return 'Esta cuenta se creó con Google, así que no tiene contraseña. Entra con el botón "Continuar con Google".';
      }
      if (metodos.includes("password")) {
        return "La contraseña no es correcta.";
      }
      // Sin métodos: o el correo no está registrado, o el proyecto oculta esa
      // información por seguridad. No se puede afirmar ninguna de las dos.
      return "No pudimos entrar con ese correo y contraseña. Revisa que estén bien escritos, o entra con Google si así te registraste en la app.";
    },

    /** Llega el token de identidad desde el botón de Google. */
    async entrarConGoogle(idToken) {
      this.intentandoGoogle = true;
      this.error = null;

      try {
        await this.logInConCredencialGoogle(idToken);
        await this.entrarSiTienePerfil();
      } catch (err) {
        this.error = this.mensajeDeError(err);
      } finally {
        this.intentandoGoogle = false;
      }
    },

    async prepararBotonGoogle() {
      try {
        await renderizarBotonGoogle(this.$refs.botonGoogle, this.entrarConGoogle);
        this.googleListo = true;
      } catch (err) {
        // Si Google no carga, el acceso con correo y contraseña sigue estando:
        // se avisa sin bloquear la pantalla.
        console.error("[portal] no se pudo preparar el acceso con Google:", err);
        this.googleError = "El acceso con Google no está disponible en este momento.";
      }
    },

    /**
     * Tramo común de los dos accesos: tener sesión de Firebase no basta, hace
     * falta un perfil en `app_usuarios`. El portal no da de alta a nadie —
     * el registro sigue viviendo en la app.
     */
    async entrarSiTienePerfil() {
      const perfil = await this.$store.dispatch("cliente/cargar", { force: true });

      if (!perfil) {
        await this.logOut();
        this.$store.dispatch("cliente/limpiar");
        this.error =
          "Esta cuenta no tiene un perfil de Recnet. Completa tu registro desde la app móvil para poder entrar.";
        return;
      }

      // Quien tiene el registro a medias entra por Network para completarlo;
      // el resto, a donde iba o a Home.
      if (this.$store.getters["cliente/perfilIncompleto"]) {
        this.$router.push({ name: "portal-cuenta" });
      } else {
        this.$router.push(this.$route.query.redirectFrom || { name: "portal" });
      }
    },

    // Firebase responde en inglés; el portal es para clientes, no para técnicos.
    // El error crudo va a la consola: el mensaje amable no debe ser el único
    // rastro cuando algo falla por una razón que no habíamos previsto.
    mensajeDeError(err) {
      console.error("[portal] fallo al iniciar sesión:", err);

      switch (err && err.code) {
        case "auth/invalid-email":
          return "El correo no es válido.";
        case "auth/too-many-requests":
          return "Demasiados intentos. Espera unos minutos e inténtalo de nuevo.";
        case "auth/network-request-failed":
          return "No hay conexión. Revisa tu internet e inténtalo de nuevo.";
        case "auth/account-exists-with-different-credential":
          return "Ya existe una cuenta con este correo creada con contraseña. Entra con tu correo y contraseña.";
        case "auth/unauthorized-domain":
          return "Este sitio no está autorizado para entrar con Google. Avísale al equipo de Recnet.";
        default:
          return typeof err === "string"
            ? err
            : "No pudimos iniciar tu sesión. Inténtalo de nuevo.";
      }
    },
  },

  mounted() {
    this.prepararBotonGoogle();
  },
};
</script>

<template>
  <div class="portal-login">
    <div class="portal-login-card">
      <div class="text-center mb-4">
        <img :src="require('@/assets/images/RCCN-LOGO.png')" alt="Recnet" class="portal-login-logo" />
        <h1 class="h4 mt-3 mb-1">Bienvenido a Recnet</h1>
        <p class="text-muted mb-0">Entra con la misma cuenta que usas en la app.</p>
      </div>

      <b-alert :show="sinPerfil && !error" variant="warning">
        Tu cuenta existe pero aún no tiene perfil de Recnet. Termina tu registro desde la app móvil.
      </b-alert>

      <b-alert :show="!!error" variant="danger">{{ error }}</b-alert>

      <b-form @submit.prevent="entrar">
        <b-form-group label="Correo electrónico" label-for="portal-email">
          <b-form-input id="portal-email" ref="campoEmail" v-model="email" type="email"
            placeholder="tucorreo@ejemplo.com" autocomplete="username"
            :class="{ 'is-invalid': enviado && $v.email.$error }" />
          <div v-if="enviado && !$v.email.required" class="invalid-feedback d-block">
            Escribe tu correo.
          </div>
          <div v-else-if="enviado && !$v.email.email" class="invalid-feedback d-block">
            El correo no es válido.
          </div>
        </b-form-group>

        <b-form-group label="Contraseña" label-for="portal-password">
          <b-form-input id="portal-password" ref="campoPassword" v-model="password" type="password"
            placeholder="Tu contraseña" autocomplete="current-password"
            :class="{ 'is-invalid': enviado && $v.password.$error }" />
          <div v-if="enviado && !$v.password.required" class="invalid-feedback d-block">
            Escribe tu contraseña.
          </div>
        </b-form-group>

        <b-button type="submit" variant="primary" block :disabled="intentando || intentandoGoogle">
          <b-spinner v-if="intentando" small class="mr-2" />
          {{ intentando ? 'Entrando…' : 'Entrar' }}
        </b-button>
      </b-form>

      <div class="separador"><span>o</span></div>

      <!-- Google dibuja aquí su propio botón: es requisito de sus pautas de
           marca y, sobre todo, es lo que permite recibir el token sin salir
           de esta página. -->
      <div ref="botonGoogle" class="contenedor-google"></div>

      <p v-if="intentandoGoogle" class="text-center text-muted small mt-2 mb-0">
        <b-spinner small class="mr-1" /> Entrando con Google…
      </p>
      <b-alert :show="!!googleError" variant="secondary" class="mt-2 mb-0 py-2 small">
        {{ googleError }}
      </b-alert>

      <p class="text-center text-muted small mt-4 mb-0">
        ¿Todavía no tienes cuenta?
        <router-link :to="{ name: 'portal-registro' }">Regístrate aquí</router-link>
      </p>
      <p class="text-center small mt-2 mb-0">
        <router-link :to="{ name: 'login' }" class="text-muted">Soy del equipo Recnet</router-link>
      </p>
    </div>
  </div>
</template>

<style scoped>
.portal-login {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  background: linear-gradient(135deg, #4b2e83 0%, #7b3fa0 100%);
}

.portal-login-card {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.portal-login-logo {
  height: 56px;
  width: auto;
}

.separador {
  display: flex;
  align-items: center;
  margin: 20px 0;
  color: #adb5bd;
  font-size: 0.85rem;
}

.separador::before,
.separador::after {
  content: "";
  flex: 1;
  border-top: 1px solid #e9ecef;
}

.separador span {
  padding: 0 12px;
}

/* El botón lo inyecta Google dentro de este contenedor; sólo se centra. */
.contenedor-google {
  display: flex;
  justify-content: center;
  min-height: 44px;
}
</style>
